import {
    getMemberAvatarUrl,
    Permission, useAuth
} from '@/contexts/AuthContext';
import type { WorkspaceRole } from '@/contexts/AuthContext/useWorkspaces';
import { useToast } from '@/contexts/ToastContext';
import Skeleton from '@common/components/Skeleton';
import {
    CaretDown,
    Check,
    MagnifyingGlass,
    Plus,
    Trash,
    User
} from '@phosphor-icons/react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import { createPortal } from 'react-dom';
import Modal from './Modal';

// --- Helper Hook for Toast Adapter ---
const useToastAdapter = () => {
    const { showToast } = useToast();
    return {
        addToast: ({
            title, description, type 
        }: { title: string, description: string, type: 'success' | 'error' | 'info' | 'warning' }) => {
            showToast(`${title}: ${description}`, type);
        }
    };
};

// --- Components ---

// 表格行骨架屏组件
const MemberRowSkeleton = () => (
    <tr>
        <td className="px-4 py-3">
            <div className="flex items-center gap-3">
                <Skeleton width="36px" height="36px" className="rounded-full" />
                <div className="min-w-0 max-w-[200px] flex flex-col gap-2">
                    <Skeleton width="120px" height="16px" />
                    <Skeleton width="160px" height="14px" />
                </div>
            </div>
        </td>
        <td className="px-4 py-3">
            <Skeleton width="80px" height="20px" />
        </td>
        <td className="px-4 py-3">
            <Skeleton width="70px" height="24px" className="rounded-full" />
        </td>
        <td className="px-4 py-3">
            <Skeleton width="100px" height="16px" />
        </td>
        <td className="px-4 py-3 text-right">
            <Skeleton width="32px" height="32px" className="inline-block" />
        </td>
    </tr>
);

const RoleSelect = ({
    member,
    currentUserRole,
    onUpdateRole,
    canChangeRole
}: {
    member: { role: WorkspaceRole | string };
    currentUserRole: WorkspaceRole | null;
    onUpdateRole: (role: WorkspaceRole) => void;
    canChangeRole: boolean;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLDivElement>(null);

    const canEdit = (() => {
        if (!canChangeRole) return false;
        if (member.role === 'OWNER') return false;
        if (!currentUserRole) return false;
        if (currentUserRole === 'ADMIN' && member.role === 'ADMIN') return false;
        return true;
    })();

    const styles: Record<WorkspaceRole, string> = {
        OWNER: "text-text-main pl-0",
        ADMIN: "text-text-main pl-0",
        MEMBER: "text-text-main pl-0"
    };

    const handleClick = useCallback(() => {
        if (!isOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setPosition({
                top: rect.bottom + 4,
                left: rect.left
            });
        }
        setIsOpen(!isOpen);
    }, [isOpen]);

    const badgeContent = (
        <span
            className={clsx(
                "inline-flex items-center gap-1.5 text-sm font-medium transition-all",
                styles[member.role as WorkspaceRole] || styles.MEMBER,
                canEdit && "hover:text-primary cursor-pointer"
            )}
        >
            <span>
                {member.role.charAt(0).toUpperCase() + member.role.slice(1).toLowerCase()}
            </span>
            {canEdit ? <CaretDown size={12} weight="bold" className="opacity-50" /> : null}
        </span>
    );

    if (!canEdit) return badgeContent;

    return (
        <>
            <div ref={triggerRef} className="relative inline-block">
                <div onClick={handleClick}>
                    {badgeContent}
                </div>
            </div>

            {isOpen ? createPortal(
                <>
                    <div className="fixed inset-0 z-[100]" onClick={() => setIsOpen(false)} />
                    <div
                        className="fixed w-32 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-[110] animate-in fade-in zoom-in-95 duration-100 text-left"
                        style={{
                            top: `${position.top}px`,
                            left: `${position.left}px`
                        }}
                    >
                        <button
                            onClick={() => { onUpdateRole('ADMIN'); setIsOpen(false); }}
                            className="w-full text-left px-3 py-2 text-sm text-text-main hover:bg-gray-50 flex items-center justify-between group"
                        >
                            <span>Admin</span>
                            {member.role === 'ADMIN' && <Check size={14} className="text-primary" weight="bold" />}
                        </button>
                        <button
                            onClick={() => { onUpdateRole('MEMBER'); setIsOpen(false); }}
                            className="w-full text-left px-3 py-2 text-sm text-text-main hover:bg-gray-50 flex items-center justify-between group"
                        >
                            <span>Member</span>
                            {member.role === 'MEMBER' && <Check size={14} className="text-primary" weight="bold" />}
                        </button>
                    </div>
                </>,
                document.body
            ) : null}
        </>
    );
};

const RoleOption = ({
    role, current, onSelect, title, desc 
}: {
    role: WorkspaceRole;
    current: WorkspaceRole;
    onSelect: (role: WorkspaceRole) => void;
    title: string;
    desc: string;
}) => (
    <div
        onClick={() => onSelect(role)}
        className={clsx(
            "p-3 rounded-lg border cursor-pointer transition-all flex items-start gap-3",
            current === role ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
        )}
    >
        <div
            className={clsx(
                "w-4 h-4 mt-0.5 rounded-full border flex items-center justify-center shrink-0",
                current === role ? "border-primary" : "border-gray-400"
            )}
        >
            {current === role && <div className="w-2 h-2 rounded-full bg-primary" />}
        </div>
        <div>
            <div className="text-sm font-medium text-text-main">{title}</div>
            <div className="text-xs text-text-sub">{desc}</div>
        </div>
    </div>
);

const MemberActions = ({
    member,
    currentUserRole,
    onRemove,
    canRemove
}: {
    member: { role: WorkspaceRole | string };
    currentUserRole: WorkspaceRole | null;
    onRemove: () => void;
    canRemove: boolean;
}) => {
    if (!canRemove) return null;
    if (member.role === 'OWNER') return null;
    if (!currentUserRole) return null;
    if (currentUserRole === 'ADMIN' && (member.role === 'ADMIN' || member.role === 'OWNER')) return null;

    return (
        <button
            onClick={onRemove}
            className="p-1.5 rounded hover:bg-red-50 text-text-sub hover:text-red-600 transition-colors"
            title="Remove Member"
        >
            <Trash size={20} weight="bold" />
        </button>
    );
};

// --- Main Component ---

const MembersSettings = () => {
    const { workspaceMembers, permissions } = useAuth();
    const {
        members,
        pendingInvites,
        isLoading,
        refresh,
        inviteMember,
        removeMember,
        updateMemberRole,
        currentUserRole,
    } = workspaceMembers;

    // 权限检查
    const canInvite = permissions.checkPermissions([Permission.MEMBER_INVITE]);
    const canRemove = permissions.checkPermissions([Permission.MEMBER_REMOVE]);
    const canChangeRole = permissions.checkPermissions([Permission.MEMBER_CHANGE_ROLE]);
    const canManageMembers = canInvite || canRemove || canChangeRole;

    const { addToast } = useToastAdapter();
    const [searchQuery, setSearchQuery] = useState('');
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState<WorkspaceRole>('MEMBER');
    const [memberToDelete, setMemberToDelete] = useState<any>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [emailError, setEmailError] = useState('');

    // 邮箱校验函数
    const validateEmail = (email: string) => {
        if (!email.trim()) {
            return 'Email is required';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return 'Please enter a valid email address';
        }
        return '';
    };

    // 处理邮箱输入变化
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInviteEmail(e.target.value);
        // 输入时清除错误提示
        if (emailError) {
            setEmailError('');
        }
    };

    // 每次加载时刷新成员数据
    useEffect(() => {
        refresh();
    }, [refresh]);

    // Filter members and pending invites by search query
    const filteredMembers = members.filter((m: any) =>
        m.displayName.toLowerCase().includes(searchQuery.toLowerCase())
        || m.email?.toLowerCase().includes(searchQuery.toLowerCase()))
        .sort((a: any, b: any) => {
            // 将当前用户放在第一位
            if (a.isCurrentUser && !b.isCurrentUser) return -1;
            if (!a.isCurrentUser && b.isCurrentUser) return 1;
            return 0;
        });

    const filteredPendingInvites = pendingInvites.filter((invite: any) =>
        invite.email.toLowerCase().includes(searchQuery.toLowerCase()));

    // Combine: members first, then pending invites
    const allRows = [
        ...filteredMembers.map((m: any) => ({ ...m, type: 'member' as const })),
        ...filteredPendingInvites.map((invite: any) => ({ ...invite, type: 'pending' as const }))
    ];

    const handleInvite = async () => {
        // 点击发送时才进行校验
        const error = validateEmail(inviteEmail);
        if (error) {
            setEmailError(error);
            return;
        }

        setIsProcessing(true);
        try {
            await inviteMember(inviteEmail, inviteRole);
            setIsInviteModalOpen(false);
            setInviteEmail('');
            setEmailError('');
            setInviteRole('MEMBER');
            addToast({
                title: 'Invitation Sent',
                description: `Successfully sent invitation to ${inviteEmail}`,
                type: 'success'
            });
        } catch (error: any) {
            addToast({
                title: 'Invitation Failed',
                description: error.message || 'Failed to invite member',
                type: 'error'
            });
        } finally {
            setIsProcessing(false);
        }
    };

    const confirmDelete = async () => {
        if (!memberToDelete) return;

        setIsProcessing(true);
        try {
            // 使用 userId 作为删除成员的 ID
            const memberId = memberToDelete.userId || String(memberToDelete.id);
            console.log('删除成员 ID:', memberId);
            await removeMember(memberId);
            setMemberToDelete(null);
            addToast({
                title: 'Member Removed',
                description: 'The member has been removed from the workspace.',
                type: 'success'
            });
        } catch (error: any) {
            addToast({
                title: 'Removal Failed',
                description: error.message || 'Failed to remove member',
                type: 'error'
            });
        } finally {
            setIsProcessing(false);
        }
    };

    const handleUpdateRole = async (memberId: string, role: WorkspaceRole) => {
        setIsProcessing(true);
        try {
            console.log('更新成员角色 ID:', memberId, '新角色:', role);
            await updateMemberRole(memberId, role);
            addToast({
                title: 'Role Updated',
                description: 'Member role has been updated successfully.',
                type: 'success'
            });
        } catch (error: any) {
            addToast({
                title: 'Update Failed',
                description: error.message || 'Failed to update member role',
                type: 'error'
            });
        } finally {
            setIsProcessing(false);
        }
    };

    // 已迁移到 AuthContext utils 中的 getMemberAvatarUrl

    const getStatusBadge = (status: string) => {
        if (status === 'INVITED' || status === 'PENDING') {
            return (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700">
                    <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                    Pending
                </span>
            );
        }
        if (status === 'ACTIVE') {
            return (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    Active
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-700">
                {status}
            </span>
        );
    };

    return (
        <div className="flex flex-col space-y-6">

            {/* Content */}
            <div className="flex flex-col space-y-6">
                {/* Toolbar */}
                <div className="flex items-center justify-between px-1">
                    <div className="relative flex-1 max-w-md">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-sub" />
                        <input
                            type="text"
                            placeholder="Search members..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        />
                    </div>
                    {canManageMembers ? (
                        <button
                            onClick={() => setIsInviteModalOpen(true)}
                            className="flex items-center gap-2 px-3 py-2 text-sm bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors shadow-sm shadow-primary/20 ml-4"
                        >
                            <Plus size={16} weight="bold" />
                            Invite Member
                        </button>
                    ) : null}
                </div>

                {/* Members List */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-50/50 border-b border-gray-100 sticky top-0 z-10">
                            <tr>
                                <th className="px-4 py-3 text-xs font-semibold text-text-sub w-[35%]">User</th>
                                <th className="px-4 py-3 text-xs font-semibold text-text-sub text-left">Role</th>
                                <th className="px-4 py-3 text-xs font-semibold text-text-sub">Status</th>
                                <th className="px-4 py-3 text-xs font-semibold text-text-sub">Joined Date</th>
                                {canManageMembers ? <th className="px-4 py-3 text-right text-xs font-semibold text-text-sub">Actions</th> : null}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {isLoading ? (
                                <>
                                    <MemberRowSkeleton />
                                    <MemberRowSkeleton />
                                    <MemberRowSkeleton />
                                </>
                            ) : (
                                allRows.map((row) => (
                                    <tr key={row.id} className="group hover:bg-gray-50/50 transition-colors">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <img
                                                    src={getMemberAvatarUrl(row)}
                                                    alt={row.type === 'member' ? row.displayName : row.email}
                                                    className="w-9 h-9 rounded-full border border-gray-200 bg-white"
                                                />
                                                <div className="min-w-0 max-w-[200px]">
                                                    <div className="font-medium text-text-main truncate">
                                                        {row.type === 'member' ? row.displayName : row.email}
                                                    </div>
                                                    {row.type === 'member' && (
                                                        <div className="text-sm text-text-sub truncate">
                                                            {row.email}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            {row.type === 'member' ? (
                                                <RoleSelect
                                                    member={row}
                                                    currentUserRole={currentUserRole}
                                                    onUpdateRole={(role) => {
                                                        handleUpdateRole(row.userId || String(row.id), role);
                                                    }}
                                                    canChangeRole={canChangeRole}
                                                />
                                            ) : (
                                                <span className="text-sm text-text-main">
                                                    {row.role.charAt(0).toUpperCase() + row.role.slice(1).toLowerCase()}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            {getStatusBadge(row.status)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-text-sub">
                                            {row.type === 'member'
                                                ? (row.joinedAt ? dayjs(row.joinedAt).format('MMM D, YYYY') : '-')
                                                : (row.createdAt ? dayjs(row.createdAt).format('MMM D, YYYY') : '-')
                                            }
                                        </td>
                                        {canManageMembers && row.type === 'member' ? (
                                            <td className="px-4 py-3 text-right">
                                                <MemberActions
                                                    member={row}
                                                    currentUserRole={currentUserRole}
                                                    onRemove={() => setMemberToDelete(row)}
                                                    canRemove={canRemove}
                                                />
                                            </td>
                                        ) : null}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    {allRows.length === 0 && !isLoading && (
                        <div className="flex flex-col items-center justify-center py-12 text-text-sub">
                            <User size={48} weight="duotone" className="mb-4 opacity-50" />
                            <p>
                                No members found matching "
                                {searchQuery}
                                "
                            </p>
                        </div>
                    )}

                    {isLoading ? (
                        <>
                            <MemberRowSkeleton />
                            <MemberRowSkeleton />
                            <MemberRowSkeleton />
                        </>
                    ) : null}
                </div>
            </div>

            {/* Invite Modal */}
            <Modal
                isOpen={isInviteModalOpen}
                onClose={() => {
                    setIsInviteModalOpen(false);
                    setInviteEmail('');
                    setEmailError('');
                }}
                title="Invite Team Member"
                width="max-w-md"
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-text-main mb-1">Email Address</label>
                        <input
                            type="email"
                            placeholder="colleague@company.com"
                            value={inviteEmail}
                            onChange={handleEmailChange}
                            className={clsx(
                                "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 outline-none transition-all",
                                emailError
                                    ? "border-red-300 focus:border-red-500"
                                    : "border-gray-300 focus:border-primary"
                            )}
                            autoFocus
                        />
                        {emailError && (
                            <p className="mt-1 text-sm text-red-600">
                                {emailError}
                            </p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-main mb-1">Role</label>
                        <div className="grid grid-cols-1 gap-2">
                            <RoleOption
                                role="ADMIN"
                                current={inviteRole}
                                onSelect={setInviteRole}
                                title="Admin"
                                desc="Manage members and settings. No access to billing or delete rights."
                            />
                            <RoleOption
                                role="MEMBER"
                                current={inviteRole}
                                onSelect={setInviteRole}
                                title="Member"
                                desc="Can only view dashboards and insights. No create or edit rights."
                            />
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            onClick={() => setIsInviteModalOpen(false)}
                            disabled={isProcessing}
                            className="px-4 py-2 text-sm font-medium text-text-sub hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleInvite}
                            disabled={!inviteEmail.trim() || isProcessing}
                            className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Send Invite
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={!!memberToDelete}
                onClose={() => setMemberToDelete(null)}
                title="Remove Member"
                width="max-w-sm"
            >
                <div className="space-y-4">
                    <p className="text-text-sub">
                        Are you sure you want to remove
                        {' '}
                        <span className="font-bold text-text-main">
                            {memberToDelete?.displayName}
                        </span>
                        ? This action cannot be undone.
                    </p>

                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            onClick={() => setMemberToDelete(null)}
                            disabled={isProcessing}
                            className="px-4 py-2 text-sm font-medium text-text-sub hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={confirmDelete}
                            disabled={isProcessing}
                            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Remove
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default MembersSettings;
